<template>
    <div>
        <card-header :title="title" :icon="icon" />

        <card-toolbar>
            <button @click="exportPlans"><i class="far fa-download"></i>Export Accounts</button>
        </card-toolbar>

        <card-body>
            <card-list>
                <template v-if="currentUser.role === 'dev' || currentUser.role === 'admin' || currentUser.role === 'collections'">

                    <container-list-item padded class="pt-3 pb-3">
                        <b-field>
                            <b-select v-model="orderBy" icon="arrow-down-short-wide" expanded>
                                <option value="activity">Sort by Status Activity</option>
                                <option value="name">Sort by Last Name</option>
                                <option value="date">Sort by Created Date</option>
                            </b-select>
                        </b-field>
                        <b-switch v-model="grouped">Group by Status</b-switch>

                    </container-list-item>
                    <template v-for="group in sortedPlans">
                        <subheader-list-item :title="group.title" :icon="group.icon" :badge="group.plans.length" />

                        <plan-tile
                            v-for="plan in group.plans"
                            :card="card"
                            :plan="plan"
                            :by-name="true"
                            :active="child && child.definition.component === 'payment-plan' && parseInt(child.props.planId) === plan.id"
                            @click="$openCard('payment-plan', {planId: plan.id}, card)"
                        />
                    </template>
                </template>
            </card-list>
        </card-body>
        <card-footer v-if="grouped">
            <strong>{{paymentPlans.length}}</strong> Total Accounts
        </card-footer>
    </div>
</template>

<script>
    import CardHeader from "@/TIER/components/CardHeader";
    import CardBody from "@/TIER/components/CardBody";
    import CardToolbar from "@/TIER/components/CardToolbar";
    import CardList from "@/TIER/components/CardList";
    import SubheaderListItem from "@/TIER/components/SubheaderListItem";
    import PlanTile from "@/components/tiles/PlanTile";
    import moment from "moment";

    import {client as http} from '../../http_client';
    import {mapGetters} from "vuex";
    import ContainerListItem from "../../TIER/components/ContainerListItem.vue";
    import CardFooter from "../../TIER/components/CardFooter.vue";

    export default {
        components: {CardFooter, ContainerListItem, PlanTile, CardHeader, CardBody, CardToolbar, CardList, SubheaderListItem},
        props: ['card', 'child', 'props'],
        data() {
            return {
                paymentPlans: [],
                title: 'Payment Plans',
                icon: 'fa-money-check-alt',
                orderBy: 'activity',
                grouped: true,
                statuses: [
                    {status: "active-good", title: 'In Good Standing'},
                    {status: "active-reminder", title: 'TPO Attempted Communication/Reminders'},
                    {status: "active-arrangement", title: 'User Made Arrangements'},
                    {status: "active-courtesy", title: 'TPO Provided Previous Courtesies'},
                    {status: "active-pending", title: 'Pending Default'},
                    {status: "default-new", title: 'In Default'},
                    {status: "default-reminder", title: 'TPO Attempted Communication/Reminders'},
                    {status: "default-arrangement", title: 'User Made Arrangements'},
                    {status: "default-pending", title: 'Pending Collections'},
                    {status: "collections-new", title: 'In Collections (New)'},
                    {status: "collections-ack", title: 'In Collections (Acknowledged)'},
                    {status: "collections-hold", title: 'On Hold'},
                    {status: "collections-info", title: 'More Information is Needed'},
                    {status: "collections-contact-attempt", title: 'Collection Attorney Attempted Communications'},
                    {status: "collections-contact", title: 'Collection Attorney Made Contact'},
                    {status: "collections-deadline", title: 'Collection Attorney Created Deadline for Resolution'},
                    {status: "collections-neg-agreement", title: 'Collection Attorney Negotiated Agreement in Place (Settled)'},
                    {status: "collections-neg-modification", title: 'Collection Attorney Negotiated Payment Modification (Settled)'},
                    {status: "collections-payment", title: 'Payment Received by Collection Attorney (Settled)'},
                    {status: "collections-payment", title: 'Client is Paying Collection Attorney (Settled)'},
                    {status: "collections-garnishment", title: 'Wage Garnishment in Place (Settled)'},
                    {status: "collections-files", title: 'Collection Attorney Filed Lawsuit'},
                    {status: "collections-summons", title: 'Summons Served'},
                    {status: "collections-court", title: 'Collection Attorney Set Court Date'},
                    {status: "collections-ruling", title: 'Collection Attorney Acquired Ruling'},
                    {status: "collections-other", title: 'Other Status'},
                ]
            }
        },
        computed: {
            ...mapGetters(['currentUser']),
            sortedPlans: function () {
                let groups = [];
                if (this.grouped) {
                    for (let i = 0; i < this.paymentPlans.length; i++) {
                        const paymentPlan = this.paymentPlans[i];

                        let group = groups.find(group => group.status === paymentPlan.status);
                        if (!group) {
                            group = {
                                title: this.statuses.find(status => status.status === paymentPlan.status)?.title || paymentPlan.status,
                                status: paymentPlan.status,
                                icon: 'fa-money-check-alt',
                                plans: this.paymentPlans.filter(plan => plan.status === paymentPlan.status)
                            };
                            groups.push(group);
                        }
                    }


                    // sort groups by status in the order of the this.statuses array
                    groups.sort((a, b) => {
                        return this.statuses.findIndex(status => status.status === a.status) - this.statuses.findIndex(status => status.status === b.status);
                    });
                } else {
                    groups.push({
                        title: 'Payment Plans',
                        icon: 'fa-money-check-alt',
                        plans: this.paymentPlans
                    });
                }

                // sort plans within each group
                groups.forEach(group => {
                    group.plans.sort((a, b) => {
                        if (this.orderBy === 'activity') {
                            return moment(b.status_updated_at).diff(a.status_updated_at);
                        } else if (this.orderBy === 'name') {
                            return a.users.last_name.localeCompare(b.users.last_name);
                        } else if (this.orderBy === 'date') {
                            return moment(b.created_at).diff(a.created_at);
                        }
                    });
                });

                return groups;
            }
        },
        methods: {
            loadPlans: function (force) {
                this.$emit('loading', true);

                switch (this.props.type) {
                    case 'pending':
                        this.title = 'Pending Accounts';
                        this.icon = 'fa-business-time';
                        break;
                    case 'suspended':
                        this.title = 'Suspended Accounts';
                        this.icon = 'fa-snooze';
                        break;
                    case 'errored':
                        this.title = 'Accounts in Error State';
                        this.icon = 'fa-exclamation-triangle';
                        break;
                    case 'missing':
                        this.title = 'Accounts Missing Details';
                        this.icon = 'fa-question-circle';
                        break;
                    case 'due':
                        this.title = 'Accounts Past Due';
                        this.icon = 'fa-bell-ring';
                        break;
                    case 'default':
                        this.title = 'Accounts in Default';
                        this.icon = 'fa-siren-on';
                        break;
                    case 'collections':
                        this.title = 'Accounts in Collections';
                        this.icon = 'fa-gavel';
                        break;
                }
                console.log(this.props.type, this.title);


                this.$emit('title', this.title);

                http.get('payment-plans/type/' + this.props.type, {force}).then(response => {
                    this.paymentPlans = response.data;
                    this.$emit('loading', false);
                }, err => {
                    console.log(err);
                });
            },
            exportPlans: function () {
                http.get(`payment-plans/export/${this.props.type}`).then(response => {
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', `accounts_${this.props.type}_${moment().format('MM_DD_YYYY')}.csv`);
                    document.body.appendChild(link);
                    link.click();
                }).catch(error => {
                    console.log(error);
                    // if error is 404
                    if (error.response.status === 404) {
                        window.alert('No accounts to export');
                        return;
                    } else {
                        window.alert('Error exporting accounts');
                        return;
                    }
                });
            },
            reload: function () {
                console.log('Reloading plans...');
                this.loadPlans(true);
            }
        },
        watch: {
            'props.type': function () {
                this.loadPlans(true);
            }
        },
        mounted: function () {
            this.loadPlans();
        }
    }
</script>
